import { Pencil, Printer, FileDown, Plus, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const digits = (v) => String(v ?? "").replace(/\D/g, "");
export const customerKey = (c) => digits(c.data.MOBILE) || String(c.data.CUSTOMER_NAME ?? "").trim().toLowerCase();

export function groupCustomers(cards) {
  const map = new Map();
  cards.forEach((c) => {
    const k = customerKey(c);
    if (!k) return;
    if (!map.has(k)) map.set(k, { key: k, name: "", mobile: "", cards: [] });
    const g = map.get(k);
    g.cards.push(c);
    g.name ||= c.data.CUSTOMER_NAME || "";
    g.mobile ||= c.data.MOBILE || "";
  });
  return [...map.values()].map((g) => ({ ...g, last: g.cards.reduce((a, c) => (c.created_at > a ? c.created_at : a), "") }))
    .sort((a, b) => b.last.localeCompare(a.last));
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

export default function CustomerHistoryDialog({ customer, onClose, onEdit, onPrint, onNew, busy }) {
  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-slate-900 border-slate-800" data-testid="customer-history-dialog">
        {customer && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl" data-testid="customer-history-name">{customer.name || "Unnamed customer"}</DialogTitle>
              <DialogDescription className="flex flex-wrap items-center gap-3">
                {customer.mobile && <span className="flex items-center gap-1 font-mono"><Phone size={12} /> {customer.mobile}</span>}
                <span data-testid="customer-history-count">{customer.cards.length} certificate{customer.cards.length === 1 ? "" : "s"}</span>
                <span>· first {fmt(customer.cards.at(-1)?.created_at)}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-slate-800">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                    <th className="px-3 py-2">Cert no.</th><th className="px-3 py-2">Item</th><th className="px-3 py-2">Metal</th>
                    <th className="px-3 py-2">Gross wt.</th><th className="px-3 py-2">Saved</th><th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customer.cards.map((c, i) => (
                    <tr key={c.id} className="border-b border-slate-800/60" data-testid={`history-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-amber-300">{c.data.CERT_NO}</td>
                      <td className="px-3 py-2 text-slate-300">{c.data.ITEM}</td>
                      <td className="px-3 py-2 text-slate-400">{c.data.METAL}</td>
                      <td className="px-3 py-2 text-slate-400">{c.data.GROSS_WT}</td>
                      <td className="px-3 py-2 text-slate-500">{fmt(c.created_at)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => onEdit(c)} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white" data-testid={`history-edit-${i}`}><Pencil size={13} /></button>
                        <button onClick={() => onPrint("print", [c])} disabled={busy} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white" data-testid={`history-print-${i}`}><Printer size={13} /></button>
                        <button onClick={() => onPrint("pdf", [c])} disabled={busy} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white" data-testid={`history-pdf-${i}`}><FileDown size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between gap-2">
              <button className="cf-btn-ghost h-9 text-xs" disabled={busy} onClick={() => onPrint("pdf", customer.cards)} data-testid="history-pdf-all"><FileDown size={13} /> PDF all</button>
              <button className="cf-btn-primary h-9 text-xs px-4" onClick={() => onNew(customer)} data-testid="history-new-certificate"><Plus size={13} /> New certificate for this customer</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
